import { useReducer, useCallback, useEffect, useContext, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Notification } from "@snapchat/snap-design-system";
import { useVendorData } from "../../../vendor-data/components";
import { globalLensDetailsReducer } from "../models";
import { type SaveLensMutation, type SaveLensMutationVariables, saveLensMutation } from "../../api/mutation";
import { type VendorDataChangedAction } from "../../../vendor-data";
import { getLensV2, type GetLensV2Query, type GetLensV2QueryVariables } from "../../api/query";
import { GlobalLensDetailsModel } from "../models/GlobalLensDetailsModel";
import { RangeModifier } from "../../../connections/models";
import { type AddLensGroupInfo } from "../../../groups/components/add-to-lensgroup-modal";
import { useAddOrUpdateConnections, useRemoveConnections } from "../../../connections/hooks";
import { LensConnectionConvertor } from "../../../connections/services/LensConnectionConvertor";
import { allChangesHaveBeenSaved, changesHaveBeenMade, lensDetailsError } from "../../../common/model";
import { type LensCompoundId } from "../../../../gql-generated/types";
import { type ComSnapCamerakitAdminV2VendorDataItem } from "../../../../gql-generated/camkit-types";

const defaultLensDetailsError = "An error occurred while updating this lens. Please try again.";

const getLensDetailsErrorMessage = (error: unknown): string =>
    error instanceof Error && error.message.length > 0 ? error.message : defaultLensDetailsError;

export const useLensDetailsV2 = (lensCompoundId: LensCompoundId, organizationId?: string) => {
    const [globalVendorData, changeGlobalVendorData] = useVendorData([]);
    const notificationApi = useContext(Notification.Context);
    const [lensDetails, dispatchLensGroups] = useReducer(
        globalLensDetailsReducer,
        null,
        () => new GlobalLensDetailsModel(),
    );
    const { id, lensSourceType, reference } = lensCompoundId;

    const {
        loading,
        data: lensData,
        error,
    } = useQuery<GetLensV2Query, GetLensV2QueryVariables>(getLensV2, {
        variables: { lensCompoundId, organizationId },
        skip: !organizationId || !id || !lensSourceType || !reference,
    });

    const [saveLens, { loading: saveLensInProgress }] = useMutation<SaveLensMutation, SaveLensMutationVariables>(
        saveLensMutation,
    );

    const { addOrUpdateConnectionForLens, loading: connectionsAreUpdating } = useAddOrUpdateConnections();

    const { removeLensConnections, loading: connectionsAreRemoving } = useRemoveConnections();

    const hasUnsavedChanges = globalVendorData.hasChanged || lensDetails.hasChanged;
    // Mirror the latest hasUnsavedChanges into a ref so the reset effect below reads it without
    // re-firing on every dirty-state flip. Writing inside useEffect keeps the render phase
    // side-effect-free (required for strict / concurrent mode).
    const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    const initializeLensDetails = useCallback(
        (getLensData: GetLensV2Query["getLensV2"]) => {
            changeGlobalVendorData({
                type: "replace",
                // vendor-data module still typed against the un-migrated (proto) VendorDataItem
                // alias. Shape matches; cast through unknown until the cleanup PR flips it.
                entries: (getLensData?.lens?.vendorData?.flatMap((vendorData) =>
                    vendorData == null ? [] : [vendorData],
                ) ?? []) as unknown as readonly ComSnapCamerakitAdminV2VendorDataItem[],
            });
            dispatchLensGroups({
                type: "initialize",
                lensWithConnections: getLensData,
            });
        },
        [changeGlobalVendorData],
    );

    useEffect(() => {
        if (hasUnsavedChanges) {
            notificationApi.info(changesHaveBeenMade);
        }
    }, [hasUnsavedChanges, notificationApi]);

    useEffect(() => {
        if (lensData && !hasUnsavedChangesRef.current) {
            initializeLensDetails(lensData.getLensV2);
        }
    }, [initializeLensDetails, lensData]);

    const changeLensInGroupVendorData = useCallback((action: VendorDataChangedAction, id: string) => {
        dispatchLensGroups({ type: "changeVendorData", vendorDataAction: action, groupId: id });
    }, []);

    const changeSchedule = useCallback((scheduleRange: RangeModifier, groupId: string) => {
        dispatchLensGroups({ type: "changeSchedule", groupId, scheduleRange });
    }, []);

    const addGroups = useCallback((groups: AddLensGroupInfo[]) => {
        dispatchLensGroups({
            type: "addGroups",
            groups,
        });
    }, []);

    const removeGroup = useCallback((groupId: string) => {
        dispatchLensGroups({
            type: "removeGroup",
            groupId,
        });
    }, []);

    const saveLensDetails = useCallback(async () => {
        try {
            if (globalVendorData.hasChanged) {
                await saveLens({
                    variables: { lensCompoundId, vendorData: globalVendorData.toDataObjects(), organizationId },
                });
            }

            await addOrUpdateConnectionForLens(
                lensCompoundId,
                lensDetails.newOrUpdatedGroups.map((groupInfo) => LensConnectionConvertor.fromLensGroupInfo(groupInfo)),
            );

            await removeLensConnections(lensCompoundId, lensDetails.removedGroupsIds);

            changeGlobalVendorData({
                type: "replace",
                entries:
                    globalVendorData.toDataObjects() as unknown as readonly ComSnapCamerakitAdminV2VendorDataItem[],
            });
            dispatchLensGroups({ type: "commitChanges" });
            notificationApi.success(allChangesHaveBeenSaved);
        } catch (error) {
            notificationApi.error(lensDetailsError(getLensDetailsErrorMessage(error)));
        }
    }, [
        globalVendorData,
        addOrUpdateConnectionForLens,
        lensCompoundId,
        lensDetails.newOrUpdatedGroups,
        lensDetails.removedGroupsIds,
        removeLensConnections,
        notificationApi,
        organizationId,
        saveLens,
    ]);

    return {
        lensDetails,
        loading,
        saveInProgress: connectionsAreUpdating || saveLensInProgress || connectionsAreRemoving,
        changeLensInGroupVendorData,
        saveLensDetails,
        changeGlobalVendorData,
        globalVendorData,
        changeSchedule,
        addGroups,
        removeGroup,
        isError: error != null,
    };
};
